import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, isCrmManager } from '../store/authStore';
import { toast } from '../hooks/useToast';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  LayoutDashboard,
  Calculator,
  User,
  Users,
  Briefcase,
  Building2,
  MapPin,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { getProfilePictureUrl } from '../utils/avatar';
import { useFeatureFlag } from '../lib/featureFlags';
import { getEnvironmentIndicator } from '../lib/environment';
import { EnvironmentCaption, EnvironmentStripe } from './EnvironmentIndicator';

function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Navy sidebar states (Figma: Nav item). Tokens live in styles/tokens.css.
const NAV_ACTIVE = 'bg-(--color-nav-item-active) text-(--color-nav-text-active)';
const NAV_IDLE = 'text-(--color-nav-text) hover:bg-(--color-nav-item-hover) hover:text-(--color-nav-text-active)';

type NavigationItem = {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  children?: Array<{
    name: string;
    href: string;
    current: boolean;
    icon?: any;
    children?: Array<{
      name: string;
      href: string;
      current: boolean;
    }>;
  }>;
} | {
  type: 'divider';
} | {
  type: 'header';
  name: string;
};


export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Initialize sidebar collapsed state from localStorage
  const getInitialCollapsedState = (): boolean => {
    try {
      const savedState = localStorage.getItem('sidebar-collapsed');
      if (savedState !== null) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Failed to parse saved sidebar collapsed state:', error);
    }
    return false;
  };

  // Initialize sidebar expanded sections from localStorage
  const getInitialExpandedSections = (): Record<string, boolean> => {
    try {
      const savedState = localStorage.getItem('sidebar-expanded-sections');
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Failed to parse saved sidebar state:', error);
    }
    return {};
  };

  // State for sidebar collapsed/expanded
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(getInitialCollapsedState);
  
  // State for sidebar navigation persistence
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(getInitialExpandedSections);

  // Toggle sidebar collapsed state
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const isAdmin = user?.role === 'account_admin';
  const crmEnabled = useFeatureFlag('crm');
  const fieldVisitsEnabled = useFeatureFlag('field_visit_requests');
  // Hide the Planning sub-section from non-managers; mirrors the Rails
  // CrmManagerGate so the nav doesn't tease links the user can't actually use.
  const crmManager = isCrmManager(user);
  // Staging/demo builds mark the sidebar; null in production and local dev.
  const envIndicator = getEnvironmentIndicator();

  // Handle section toggle
  const toggleSection = (sectionName: string) => {
    // If sidebar is collapsed, expand it first
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
    }
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Auto-expand user section when on profile page (only when sidebar is expanded)
  useEffect(() => {
    const isProfile = location.pathname === '/profile';
    if (isProfile && !sidebarCollapsed) {
      setExpandedSections(prev => ({
        ...prev,
        'User': true
      }));
    } else if (sidebarCollapsed) {
      // Collapse user section when sidebar collapses
      setExpandedSections(prev => ({
        ...prev,
        'User': false
      }));
    }
  }, [location.pathname, sidebarCollapsed]);

  // Save sidebar expanded sections state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-expanded-sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // Save sidebar collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Make navigation reactive to location changes
  const navigation: NavigationItem[] = useMemo(() => {
    const pathname = location.pathname;
    
    // Helper function to check if any child path matches
    const hasActiveChild = (children: Array<{ href: string; current?: boolean; children?: Array<{ href: string; current?: boolean }> }>): boolean => {
      return children.some(child => {
        if (child.href === pathname) return true;
        if (child.children && hasActiveChild(child.children)) return true;
        return false;
      });
    };
    
    const items: NavigationItem[] = [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, current: pathname === '/dashboard' },
      {
        name: 'Accounting',
        href: '/accounting',
        icon: Calculator,
        children: [
          { name: 'Commission', href: '/accounting/manual-commission-reporting', current: pathname === '/accounting/manual-commission-reporting' },
          { name: 'Partners', href: '/accounting/partners', current: pathname.startsWith('/accounting/partners') },
        ],
        // Only highlight parent if it's the exact path (when expanded) or if sidebar is collapsed and a child is active
        current: pathname === '/accounting'
      },
      ...(crmEnabled ? [
        {
          name: 'CRM',
          href: '/crm/operators',
          icon: Briefcase,
          children: [
            { name: 'Operators', href: '/crm/operators', current: pathname === '/crm/operators' || pathname.startsWith('/crm/operators/') },
            { name: 'Contacts', href: '/crm/contacts', current: pathname === '/crm/contacts' },
            { name: 'Opportunities', href: '/crm/opportunities', current: pathname === '/crm/opportunities' || pathname.startsWith('/crm/opportunities/') },
            { name: 'Activities', href: '/crm/activities', current: pathname === '/crm/activities' },
            { name: 'Sales Logs', href: '/crm/sales-logs', current: pathname === '/crm/sales-logs' || pathname.startsWith('/crm/sales-logs/') },
            { name: 'Products', href: '/crm/products', current: pathname === '/crm/products' },
            // Planning is manager-only — same gate as Rails CrmManagerGate and
            // the <ProtectedRoute requireCrmManager /> wrapper in App.tsx.
            ...(crmManager ? [
              {
                name: 'Planning',
                href: '/crm/call-targets',
                current:
                  pathname === '/crm/call-targets' ||
                  pathname === '/crm/distributor-initiatives' ||
                  pathname === '/crm/sales-targets' ||
                  pathname === '/crm/client-manager-targets' ||
                  pathname === '/crm/goals',
                children: [
                  { name: 'Call targets', href: '/crm/call-targets', current: pathname === '/crm/call-targets' },
                  { name: 'Distributor initiatives', href: '/crm/distributor-initiatives', current: pathname === '/crm/distributor-initiatives' },
                  { name: 'Sales Targets', href: '/crm/sales-targets', current: pathname === '/crm/sales-targets' },
                  { name: 'CM Targets', href: '/crm/client-manager-targets', current: pathname === '/crm/client-manager-targets' },
                  { name: 'Goals', href: '/crm/goals', current: pathname === '/crm/goals' },
                ],
              },
            ] : []),
          ],
          current: pathname === '/crm',
        },
      ] : []),
      // Field Visit Requests (P-EXE-88). The request list and detail page come
      // with ENG-751; until then the entry goes straight to the form.
      ...(fieldVisitsEnabled ? [
        {
          name: 'Field visits',
          href: '/field-visits/new',
          icon: MapPin,
          current: pathname.startsWith('/field-visits'),
        },
      ] : []),
      ...(isAdmin ? [
        { type: 'divider' as const },
        { type: 'header' as const, name: 'Account Admin' },
        { name: 'Users', href: '/admin/users', icon: Users, current: pathname === '/admin/users' },
        ...(crmEnabled ? [
          { name: 'Divisions', href: '/admin/divisions', icon: Building2, current: pathname === '/admin/divisions' },
        ] : []),
      ] : []),
    ];

    // Update parent items to be current only if sidebar is collapsed and a child is active
    return items.map(item => {
      if ('type' in item) return item;
      if (item.children) {
        const hasActive = hasActiveChild(item.children);
        // If sidebar is collapsed and a child is active, highlight the parent
        // If sidebar is expanded, only highlight parent if it's the exact path match
        if (sidebarCollapsed && hasActive) {
          return { ...item, current: true };
        }
        // If expanded, keep the original current value (which is only true for exact path matches)
      }
      return item;
    });
  }, [location.pathname, isAdmin, sidebarCollapsed, crmEnabled, crmManager, fieldVisitsEnabled]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Mobile menu button. Carries the env stripe so phones see it before opening the drawer. */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 overflow-hidden bg-white rounded-lg shadow-lg border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
        aria-label="Open sidebar"
      >
        <EnvironmentStripe indicator={envIndicator} testId="environment-stripe-mobile" />
        <Menu className="h-6 w-6" />
      </button>

      {/* Main layout with sidebar and content */}
      <div className="flex flex-1">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-(--color-bg-scrim) lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={classNames(
            'fixed top-0 bottom-0 left-0 z-40 bg-(--color-nav-bg) transform transition-all duration-300 ease-in-out lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            sidebarCollapsed ? 'w-16 lg:w-16' : 'w-64'
          )}
        >
          <EnvironmentStripe indicator={envIndicator} />
          <div className={classNames(
            'flex flex-col h-full',
            sidebarCollapsed ? 'px-3' : 'px-6'
          )}>
            {/* Logo and hamburger menu container - Fixed at top */}
            <div className={classNames(
              'flex h-16 shrink-0 items-center',
              sidebarCollapsed ? 'flex-col justify-center gap-1.5' : ''
            )}>
              {sidebarCollapsed ? (
                <>
                  <button
                    onClick={toggleSidebarCollapse}
                    className="text-(--color-nav-icon) hover:text-(--color-nav-text-active) transition-colors"
                    aria-label="Expand sidebar"
                  >
                    <Menu className="h-6 w-6" />
                  </button>
                  <EnvironmentCaption indicator={envIndicator} collapsed />
                </>
              ) : (
                <>
                  <div className="flex flex-col">
                    <img
                      src="/axis360-white.svg"
                      alt="Axis360"
                      className="h-8 w-auto self-start"
                    />
                    <EnvironmentCaption indicator={envIndicator} />
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {/* Desktop collapse button */}
                    <button
                      onClick={toggleSidebarCollapse}
                      className="text-(--color-nav-icon) hover:text-(--color-nav-text-active) transition-colors lg:block hidden"
                      aria-label="Collapse sidebar"
                    >
                      <Menu className="h-6 w-6" />
                    </button>
                    {/* Mobile close button */}
                    <button
                      onClick={() => setSidebarOpen(false)}
                      className="text-(--color-nav-icon) hover:text-(--color-nav-text-active) transition-colors lg:hidden"
                      aria-label="Close sidebar"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Navigation - Scrollable middle section */}
            <nav className="flex-1 overflow-y-auto scrollbar-hide">
              <ul role="list" className="flex flex-col gap-y-7 py-4">
                <li>
                  <ul role="list" className="space-y-1">
                    {navigation.map((item, index) => {
                      if ('type' in item && item.type === 'divider') {
                        return (
                          <li key={`divider-${index}`}>
                            <div className="border-t border-(--color-nav-border) my-2"></div>
                          </li>
                        );
                      }
                      
                      if ('type' in item && item.type === 'header') {
                        if (sidebarCollapsed) return null;
                        return (
                          <li key={`header-${index}`}>
                            <div className="px-2 py-1">
                              <h3 className="text-xs font-semibold text-(--color-nav-section) uppercase tracking-wider">
                                {item.name}
                              </h3>
                            </div>
                          </li>
                        );
                      }
                      
                      const navItem = item as Exclude<NavigationItem, { type: 'divider' | 'header' }>;
                      
                      return (
                        <li key={navItem.name}>
                          {!navItem.children ? (
                            <NavLink
                              to={navItem.href}
                              onClick={() => {
                                // Close sidebar on mobile when clicking a nav link
                                if (window.innerWidth < 1024) {
                                  setSidebarOpen(false);
                                }
                              }}
                              className={({ isActive }) => classNames(
                                (isActive || navItem.current) ? NAV_ACTIVE : NAV_IDLE,
                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold min-w-0 items-center',
                                sidebarCollapsed && 'justify-center'
                              )}
                              title={sidebarCollapsed ? navItem.name : undefined}
                            >
                              {({ isActive }) => (
                                <>
                                  <navItem.icon className={classNames(
                                    'h-5 w-5 shrink-0',
                                    (isActive || navItem.current) ? 'text-(--color-nav-icon-active)' : 'text-(--color-nav-icon)',
                                    sidebarCollapsed ? 'h-6 w-6' : ''
                                  )} />
                                  {!sidebarCollapsed && <span className="truncate">{navItem.name}</span>}
                                </>
                              )}
                            </NavLink>
                          ) : (
                            <Disclosure 
                              as="div" 
                              key={`${navItem.name}-${sidebarCollapsed}-${expandedSections[navItem.name]}`}
                              defaultOpen={expandedSections[navItem.name]}
                            >
                              <DisclosureButton
                                onClick={() => {
                                  // If sidebar is collapsed, expand it when clicking
                                  if (sidebarCollapsed) {
                                    setSidebarCollapsed(false);
                                    // Set the section as expanded when expanding sidebar from collapsed state
                                    setExpandedSections(prev => ({
                                      ...prev,
                                      [navItem.name]: true
                                    }));
                                  } else {
                                    toggleSection(navItem.name);
                                  }
                                }}
                                className={classNames(
                                  navItem.current ? NAV_ACTIVE : NAV_IDLE,
                                  'group flex w-full items-center gap-x-3 rounded-md p-2 pr-3 text-left text-sm/6 font-semibold',
                                  sidebarCollapsed && 'justify-center'
                                )}
                                title={sidebarCollapsed ? navItem.name : undefined}
                              >
                                <navItem.icon className={classNames(
                                  'h-5 w-5 shrink-0',
                                  navItem.current ? 'text-(--color-nav-icon-active)' : 'text-(--color-nav-icon)',
                                  sidebarCollapsed ? 'h-6 w-6' : ''
                                )} />
                                {!sidebarCollapsed && (
                                  <>
                                    <span className="truncate flex-1 min-w-0">{navItem.name}</span>
                                    <ChevronRightIcon
                                      aria-hidden="true"
                                      className={classNames(
                                        'ml-auto size-5 shrink-0 text-(--color-nav-icon) transition-transform duration-200 flex-shrink-0',
                                        expandedSections[navItem.name] ? 'rotate-90 text-(--color-nav-text)' : ''
                                      )}
                                    />
                                  </>
                                )}
                              </DisclosureButton>
                              {!sidebarCollapsed && (
                                <DisclosurePanel as="ul" className="mt-1">
                                  {navItem.children.map((subItem) => {
                                    // Check if this subItem has its own children (nested)
                                    if (subItem.children && subItem.children.length > 0) {
                                      return (
                                        <li key={subItem.name}>
                                          <Disclosure as="div" defaultOpen={expandedSections[subItem.name]}>
                                            <DisclosureButton
                                              onClick={() => toggleSection(subItem.name)}
                                              className={classNames(
                                                subItem.current ? NAV_ACTIVE : NAV_IDLE,
                                                'group flex w-full items-center gap-x-3 rounded-md py-2 pr-2 pl-9 text-left text-sm/6 min-w-0',
                                              )}
                                            >
                                              {subItem.icon && (
                                                <subItem.icon className="h-4 w-4 shrink-0 text-(--color-nav-icon)" />
                                              )}
                                              <span className="truncate flex-1 min-w-0">{subItem.name}</span>
                                              <ChevronRightIcon
                                                aria-hidden="true"
                                                className={classNames(
                                                  'ml-auto size-4 shrink-0 text-(--color-nav-icon) transition-transform duration-200 flex-shrink-0',
                                                  expandedSections[subItem.name] ? 'rotate-90 text-(--color-nav-text)' : ''
                                                )}
                                              />
                                            </DisclosureButton>
                                            <DisclosurePanel as="ul" className="mt-1 px-2">
                                              {subItem.children.map((nestedItem) => (
                                                <li key={nestedItem.name}>
                                                  <NavLink
                                                    to={nestedItem.href}
                                                    onClick={() => {
                                                      // Close sidebar on mobile when clicking a nested nav link
                                                      if (window.innerWidth < 1024) {
                                                        setSidebarOpen(false);
                                                      }
                                                    }}
                                                    className={({ isActive }) => classNames(
                                                      (isActive || nestedItem.current)
                                                        ? NAV_ACTIVE
                                                        : NAV_IDLE,
                                                      'block rounded-md p-2 pl-16 text-sm/6 truncate',
                                                    )}
                                                  >
                                                    {nestedItem.name}
                                                  </NavLink>
                                                </li>
                                              ))}
                                            </DisclosurePanel>
                                          </Disclosure>
                                        </li>
                                      );
                                    }
                                    // Regular child item without nested children
                                    return (
                                      <li key={subItem.name}>
                                        <NavLink
                                          to={subItem.href}
                                          onClick={() => {
                                            // Close sidebar on mobile when clicking a nested nav link
                                            if (window.innerWidth < 1024) {
                                              setSidebarOpen(false);
                                            }
                                          }}
                                          className={({ isActive }) => classNames(
                                            (isActive || subItem.current)
                                              ? NAV_ACTIVE
                                              : NAV_IDLE,
                                            'block rounded-md p-2 pl-9 text-sm/6 truncate',
                                          )}
                                        >
                                          {subItem.name}
                                        </NavLink>
                                      </li>
                                    );
                                  })}
                                </DisclosurePanel>
                              )}
                            </Disclosure>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            </nav>

            {/* User dropdown - Fixed at bottom */}
            <div className={classNames(
              'shrink-0 border-t border-(--color-nav-border) mt-auto',
              sidebarCollapsed ? '-mx-3' : '-mx-6'
            )}>
              {(() => {
                const isProfile = location.pathname === '/profile';
                // Only show expanded if sidebar is not collapsed and either manually expanded or on profile
                const userSectionExpanded = !sidebarCollapsed && (expandedSections['User'] ?? isProfile);
                
                return (
                  <Disclosure 
                    as="div" 
                    key={`user-dropdown-${sidebarCollapsed}`}
                    defaultOpen={userSectionExpanded}
                  >
                    {({ open }) => (
                      <div className={open ? 'bg-(--color-nav-item-hover)' : ''}>
                        <DisclosureButton
                          onClick={() => {
                            // If sidebar is collapsed, expand it when clicking chevron
                            if (sidebarCollapsed) {
                              setSidebarCollapsed(false);
                              // Set the user section as expanded when expanding sidebar from collapsed state
                              setExpandedSections(prev => ({
                                ...prev,
                                'User': true
                              }));
                            } else {
                              toggleSection('User');
                            }
                          }}
                          className={classNames(
                            (sidebarCollapsed && isProfile) ? NAV_ACTIVE : NAV_IDLE,
                            'flex items-center gap-x-4 py-3 text-sm/6 font-semibold w-full overflow-hidden min-w-0',
                            sidebarCollapsed ? 'justify-center px-3' : 'px-6',
                            open && 'bg-(--color-nav-item-hover)'
                          )}
                          aria-expanded={open}
                          aria-haspopup="true"
                          title={sidebarCollapsed ? user?.full_name : undefined}
                        >
                      <img
                        src={getProfilePictureUrl(user?.profile_picture_url)}
                        alt={user?.full_name || 'User'}
                        className="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5 object-cover shrink-0"
                      />
                      {!sidebarCollapsed && (
                        <>
                          <span className="sr-only">Your profile</span>
                          <span aria-hidden="true" className="flex-1 text-left truncate min-w-0">
                            {user?.full_name || 'User'}
                          </span>
                          <ChevronRightIcon
                            aria-hidden="true"
                            className={classNames(
                              'ml-auto size-5 shrink-0 text-(--color-nav-icon) transition-transform duration-200',
                              open ? 'rotate-90 text-(--color-nav-text)' : ''
                            )}
                          />
                        </>
                      )}
                    </DisclosureButton>

                    <DisclosurePanel as="div" className="px-6 pb-3">
                      <div className="space-y-1">
                        {/* Profile link */}
                        <NavLink
                          to="/profile"
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                          className={({ isActive }) => classNames(
                            isActive ? NAV_ACTIVE : NAV_IDLE,
                            'flex items-center gap-3 w-full rounded-md px-2 py-2 text-sm transition-colors'
                          )}
                        >
                          <User className="h-5 w-5 text-(--color-nav-icon)" />
                          Profile
                        </NavLink>

                        {/* Divider */}
                        <div className="border-t border-(--color-nav-border) my-1"></div>

                        {/* Logout button */}
                        <button
                          onClick={() => {
                            setSidebarOpen(false);
                            handleLogout();
                          }}
                          className="flex items-center gap-3 w-full px-2 py-2 text-sm text-(--color-nav-text) hover:bg-(--color-nav-item-hover) hover:text-(--color-nav-text-active) rounded-md transition-colors"
                        >
                          <LogOut className="h-5 w-5 text-(--color-nav-icon)" />
                          Logout
                        </button>
                      </div>
                    </DisclosurePanel>
                  </div>
                )}
              </Disclosure>
                );
              })()}
            </div>

          </div>
        </aside>

        {/* Main content area */}
        <main className={classNames(
          'flex-1 pt-20 lg:pt-6 px-6 pb-6 overflow-auto transition-all duration-300',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
